import React, { useEffect, useMemo, useState } from "react";
import { Bell, Megaphone, Send, Smartphone, Tag } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";

const categories = [
  { id: "general", label: "General message" },
  { id: "offer", label: "Offer / promo" },
  { id: "announcement", label: "Announcement" },
];

export default function AdminNotificationsPage() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, pushEnabledUsers: 0, totalPushTokens: 0 });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [audience, setAudience] = useState("all");
  const [selectedUids, setSelectedUids] = useState([]);
  const [busy, setBusy] = useState("");

  async function load() {
    const [usersRes, statsRes] = await Promise.all([
      adminService.getUsers(),
      adminService.getNotificationStats(),
    ]);
    if (usersRes.ok) setUsers(usersRes.data?.items || []);
    if (statsRes.ok) setStats(statsRes.data || {});
  }

  useEffect(() => {
    load();
  }, []);

  const pushReadyUsers = useMemo(
    () => users.filter((user) => Number(user.pushTokenCount || 0) > 0),
    [users]
  );

  function toggleUser(uid) {
    setSelectedUids((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  }

  async function handleTestPush() {
    setBusy("test");
    const res = await adminService.sendTestNotification({
      title: title.trim() || "Test notification",
      body: body.trim() || "Smart Farming push is working on your phone.",
      category,
    });
    setBusy("");
    if (!res.ok) {
      toast.error(res.error || "Test notification failed");
      return;
    }
    toast.success("Test notification sent to your account. Check your phone.");
  }

  async function handleBroadcast() {
    if (!title.trim() || !body.trim()) {
      toast.warning("Title and message are required.");
      return;
    }
    if (audience === "selected" && !selectedUids.length) {
      toast.warning("Select at least one user.");
      return;
    }

    setBusy("broadcast");
    const res = await adminService.broadcastNotification({
      title: title.trim(),
      body: body.trim(),
      category,
      targetUids: audience === "selected" ? selectedUids : [],
    });
    setBusy("");
    if (!res.ok) {
      toast.error(res.error || "Broadcast failed");
      return;
    }
    const data = res.data || {};
    toast.success(
      `Sent to ${data.recipients || 0} users (${data.pushSent || 0} with push enabled).`
    );
    setTitle("");
    setBody("");
    setSelectedUids([]);
    load();
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Push"
        highlight="notifications."
        description="Send offers, announcements, and messages to registered app users. SMS alerts are coming soon."
      />
      <PageSection variant="content" className="pb-28">
        <div className="mx-auto max-w-7xl w-full min-w-0 grid gap-6">
          <AdminNav />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 min-w-0">
            <Card className="shadow-soft min-w-0">
              <CardContent className="pt-6">
                <div className="text-2xl font-semibold">{stats.totalUsers || 0}</div>
                <div className="text-sm text-muted-foreground">Registered users</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft min-w-0">
              <CardContent className="pt-6">
                <div className="text-2xl font-semibold">{stats.pushEnabledUsers || 0}</div>
                <div className="text-sm text-muted-foreground">Users with app push enabled</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft opacity-80 min-w-0">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-2xl font-semibold text-muted-foreground">Soon</div>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Coming soon
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">SMS notifications (not available yet)</div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-soft border-dashed border-border/80 min-w-0 overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Smartphone className="h-4 w-4 shrink-0" />
                SMS notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground leading-relaxed">
              SMS alerts for offers and critical updates will be available in a future release. This section is
              preview-only and cannot send SMS yet.
            </CardContent>
          </Card>

          <Card className="shadow-soft min-w-0 overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Megaphone className="h-5 w-5 shrink-0" />
                Send app notification
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 min-w-0">
                {categories.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCategory(item.id)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                      category === item.id
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border text-muted-foreground hover:bg-accent/5"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <input
                className="w-full max-w-full box-border h-11 px-4 rounded-2xl border border-border bg-background text-sm"
                placeholder="Notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <textarea
                className="w-full max-w-full box-border min-h-28 px-4 py-3 rounded-2xl border border-border bg-background text-sm resize-y"
                placeholder="Message for users (offer details, update, promo, etc.)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => setAudience("all")}
                  className={`w-full rounded-2xl border px-4 py-3 text-sm ${
                    audience === "all" ? "border-accent bg-accent/10 text-accent" : "border-border"
                  }`}
                >
                  All registered users
                </button>
                <button
                  type="button"
                  onClick={() => setAudience("selected")}
                  className={`w-full rounded-2xl border px-4 py-3 text-sm ${
                    audience === "selected" ? "border-accent bg-accent/10 text-accent" : "border-border"
                  }`}
                >
                  Selected users only
                </button>
              </div>

              {audience === "selected" ? (
                <div className="rounded-2xl border border-border p-3 max-h-56 overflow-y-auto grid gap-2 min-w-0">
                  {users.map((user) => (
                    <label key={user.uid} className="flex items-center gap-3 text-sm cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedUids.includes(user.uid)}
                        onChange={() => toggleUser(user.uid)}
                        className="shrink-0"
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {user.displayName || user.email || user.uid}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {user.pushTokenCount ? `${user.pushTokenCount} push` : "no push"}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  {pushReadyUsers.length} of {users.length} users currently have push enabled on mobile.
                </div>
              )}

              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={busy === "test"}
                  onClick={handleTestPush}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Bell className="h-4 w-4" />
                  {busy === "test" ? "Sending..." : "Send test to me"}
                </Button>
                <Button
                  disabled={busy === "broadcast"}
                  onClick={handleBroadcast}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Send className="h-4 w-4" />
                  {busy === "broadcast" ? "Sending..." : "Send notification"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft min-w-0 overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag className="h-4 w-4 shrink-0" />
                Push-ready users
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 min-w-0">
              {pushReadyUsers.length ? (
                pushReadyUsers.map((user) => (
                  <div
                    key={user.uid}
                    className="flex items-center justify-between gap-3 text-sm border-b border-border/60 py-2 last:border-0 min-w-0"
                  >
                    <span className="truncate">{user.displayName || user.email || user.uid}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{user.pushTokenCount} device(s)</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No users have enabled push yet. Users must open the mobile app and allow notifications.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </PageSection>
    </PageShell>
  );
}
